import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import {
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import { environment, HTTP_AUTH_INTERCEPTOR } from '@ups/xplat/core';
import { AuthenticationService } from '@ups/security';
import { MsalSecurityIdentitySource } from './msal-authentication.service';

// Use window.location.origin so MSAL adapts to whichever host the app is served
// from (localhost dev, LAN IP, staging, production). Each origin must still be
// registered as a valid redirect URI in the Entra app registration.
const REDIRECT_URI =
  typeof window !== 'undefined'
    ? window.location.origin
    : 'http://localhost:4213';

// Hosted in the Electron Control Center? The preload exposes window.rynCC
// (conformance-checked, same rule as RynCcService.detect: a truthy
// non-conforming global must not read as Electron). The shell is a
// single-operator surface with a persistent profile, so login UX differs
// from the shared-browser web app:
//  - token cache goes to LocalStorage (Electron's userData persists it), so
//    a cold boot reuses cached tokens and silent refresh instead of a
//    round-trip;
//  - the guard drops the forced account picker and rides the Entra session
//    cookie straight through when a redirect does happen.
// Plain web keeps SessionStorage + select_account exactly as before.
const IS_ELECTRON_SHELL = (() => {
  const w = globalThis as {
    rynCC?: { meta?: unknown; daemons?: unknown };
  };
  return (
    typeof w.rynCC?.meta === 'function' &&
    typeof w.rynCC?.daemons === 'function'
  );
})();

const msalLogLevel = parseInt(
  localStorage.getItem('msal-log-level') ?? String(LogLevel.Info),
  10
);
const msalLogPII =
  localStorage.getItem('msal-log-pii') === 'true' ? true : false;

console.log(
  'msalLogLevel',
  LogLevel[msalLogLevel] ?? msalLogLevel,
  'msalLogPII',
  msalLogPII
);

export function msalInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.entraId!.clientId!,
      authority: environment.entraId!.authority!,
      redirectUri: REDIRECT_URI,
      postLogoutRedirectUri: REDIRECT_URI,
    },
    cache: {
      // Stays SessionStorage in BOTH shells. A LocalStorage cache was tried
      // for the Electron shell (persist tokens across boots) and broke the
      // SECOND boot: with accounts already in cache, bootstrap dies with
      // uninitialized_public_client_application (something consumes MSAL
      // before initialize() settles; the empty-cache path never hits it).
      // The shell's cold-boot SSO comes from the Entra session cookie
      // instead: the guard redirect completes silently (no forced prompt
      // below), which is the whole UX win at a ~1s hop.
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      allowPlatformBroker: false,
      loggerOptions: {
        loggerCallback: (_level: LogLevel, message: string) => {
          switch (_level) {
            case LogLevel.Trace:
            case LogLevel.Verbose:
              // eslint-disable-next-line no-console -- pre-commit-allow: MSAL logger writes to console by design (shared msal.config pattern)
              console.debug(message);
              break;
            case LogLevel.Info:
              console.log(message);
              break;
            case LogLevel.Warning:
              console.warn(message);
              break;
            case LogLevel.Error:
              console.error(message);
              break;
          }
        },
        logLevel: msalLogLevel,
        piiLoggingEnabled: msalLogPII,
      },
    },
  });
}

export function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map([
      ['https://graph.microsoft.com/v1.0/*', ['user.read']],
      [`${environment.urls!.bridgeAPI}/*`, []], // no msal auth
      // note: this won't work on firefox because... just because micro$oft can't be bothered...
      // https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/7275
      ['https://*', [`${environment.entraId?.audience}/Access.All`]],
    ]),
    strictMatching: false,
  };
}

export function msalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: (msalService: MsalService) => {
      const scopes = ['user.read'];
      const accounts = msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        return { scopes, account: accounts[0] };
      }

      // Electron shell: rely on Entra session cookie (no forced account picker).
      if (IS_ELECTRON_SHELL) {
        return { scopes };
      }

      // Web deep links (e.g. ADO Replay: URLs) often open in a new tab with an
      // empty MSAL SessionStorage cache even when the user is signed in to Entra
      // in the browser. Forcing select_account every time looks like "must log
      // in again"; omit prompt so SSO can complete silently when a session exists.
      return { scopes };
    },
    loginFailedRoute: '/login-failed',
  };
}

export const msalProviders = [
  { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory },
  { provide: MSAL_GUARD_CONFIG, useFactory: msalGuardConfigFactory },
  {
    provide: MSAL_INTERCEPTOR_CONFIG,
    useFactory: msalInterceptorConfigFactory,
  },
  { provide: HTTP_AUTH_INTERCEPTOR, useClass: MsalInterceptor },
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  {
    provide: AuthenticationService,
    useExisting: MsalSecurityIdentitySource,
  },
];

export { MsalGuard };
